import { type FC, useCallback, useEffect, useState, useRef } from 'react';
import ReactFlow, {
  Background,
  Controls,
  BackgroundVariant,
  ReactFlowProvider,
  type Connection,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { nodeTypes } from './nodes';
import { edgeTypes } from './edges';
import CanvasToolbar from './controls/CanvasToolbar';
import LinkTypeSelector from './controls/LinkTypeSelector';
import EdgeContextMenu from './controls/EdgeContextMenu';
import MiniMap from './controls/MiniMap';
import Legend from './controls/Legend';
import ViewSwitcher, { type CanvasView } from './controls/ViewSwitcher';
import CanvasContextMenu from './controls/CanvasContextMenu';
import TreeView from './views/TreeView';
import TableView from './views/TableView';
import { useCanvas } from '@/hooks/useCanvas';
import { NodeType } from '@/types/node.types';
import { LinkType } from '@/types/link.types';
import type { LinkData } from '@/types/link.types';
import { useHistoryStore } from '@/stores/history.store';
import { useCanvasStore } from '@/stores/canvas.store';
import nodesApi from '@/api/nodes.api';
import { useCollaboration } from '@/collaboration/useCollaboration';
import RemoteCursors from './controls/RemoteCursors';
import NodeInspector from './controls/NodeInspector';
import HistoryPanel from './controls/HistoryPanel';
import ExportMenu from './controls/ExportMenu';
import { computeAutoLayout, type LayoutAlgorithm, type LayoutDirection } from '@/services/autoLayout';
import LayoutPickerDialog from '@/components/ui/LayoutPickerDialog';

interface StratMapCanvasProps {
  canvasId: string;
  workspaceId?: string;
}

const StratMapCanvasInner: FC<StratMapCanvasProps> = ({ canvasId, workspaceId }) => {
  const {
    nodes,
    edges,
    isLoading,
    error,
    onNodesChange,
    onEdgesChange,
    createNode,
    removeNode,
    removeEdge,
    updateEdge,
    addEdge: addLink,
  } = useCanvas(canvasId);

  const { pushSnapshot, undo, redo, canUndo, canRedo } = useHistoryStore();
  const setCanvasState = useCanvasStore.setState;

  // Pending connection waiting for link type selection
  const [pendingConnection, setPendingConnection] = useState<Connection | null>(null);
  const [selectorPosition, setSelectorPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Snapshot taken at drag start to historise position changes
  const dragSnapshotRef = useRef<{ nodes: typeof nodes; edges: typeof edges } | null>(null);

  // Collaboration
  const { isConnected, connectedUsers, remoteCursors } = useCollaboration(canvasId);

  // Comments panel — opens when a node is selected
  // Selected node id → drives the right-side NodeInspector panel (Details / Relations / Comments tabs).
  const [inspectedNodeId, setInspectedNodeId] = useState<string | null>(null);

  // History panel
  const [historyOpen, setHistoryOpen] = useState(false);

  // Edge context menu
  const [selectedEdge, setSelectedEdge] = useState<{ id: string; type: LinkType } | null>(null);
  const [edgeMenuPosition, setEdgeMenuPosition] = useState({ x: 0, y: 0 });

  // View switcher
  const [activeView, setActiveView] = useState<CanvasView>('canvas');

  // Auto-layout picker
  const [layoutPickerOpen, setLayoutPickerOpen] = useState(false);
  const [layoutBusy, setLayoutBusy] = useState(false);
  const [lastLayoutAlgorithm, setLastLayoutAlgorithm] = useState<LayoutAlgorithm>('layered');
  const [lastLayoutDirection, setLastLayoutDirection] = useState<LayoutDirection>('TB');

  // Context menu
  const [contextMenu, setContextMenu] = useState<{
    screen: { x: number; y: number };
    flow: { x: number; y: number };
  } | null>(null);

  const reactFlowInstance = useReactFlow();

  const saveSnapshot = useCallback(() => {
    pushSnapshot({ nodes, edges });
  }, [nodes, edges, pushSnapshot]);

  const focusOnCreatedNode = useCallback(
    (position: { x: number; y: number }, nodeId?: string) => {
      // Node tile is ~240×140. Target the tile center so it sits in the middle of the view.
      const zoom = Math.max(reactFlowInstance.getZoom(), 1);
      reactFlowInstance.setCenter(position.x + 120, position.y + 70, {
        zoom,
        duration: 350,
      });
      if (nodeId) setInspectedNodeId(nodeId);
    },
    [reactFlowInstance],
  );

  const handleAddNode = useCallback(
    async (nodeType: NodeType) => {
      saveSnapshot();
      // Place the new node near the center of the current viewport (with a
      // small jitter so repeated clicks don't overlap at the exact same spot).
      const container = containerRef.current;
      const rect = container?.getBoundingClientRect();
      const screenCenter = rect
        ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
        : { x: window.innerWidth / 2, y: window.innerHeight / 2 };
      const flowCenter = reactFlowInstance.screenToFlowPosition(screenCenter);
      const jitter = () => (Math.random() - 0.5) * 80;
      const position = {
        x: flowCenter.x - 120 + jitter(),
        y: flowCenter.y - 70 + jitter(),
      };
      const created = await createNode(nodeType, position);
      focusOnCreatedNode(position, created?.id);
    },
    [createNode, saveSnapshot, reactFlowInstance, focusOnCreatedNode],
  );

  const handleAddNodeAtPosition = useCallback(
    async (nodeType: NodeType, position: { x: number; y: number }) => {
      saveSnapshot();
      const created = await createNode(nodeType, position);
      focusOnCreatedNode(position, created?.id);
    },
    [createNode, saveSnapshot, focusOnCreatedNode],
  );

  const handleOpenLayoutPicker = useCallback(() => {
    if (nodes.length === 0) return;
    setLayoutPickerOpen(true);
  }, [nodes.length]);

  const handleApplyLayout = useCallback(
    async (algorithm: LayoutAlgorithm, direction: LayoutDirection) => {
      if (nodes.length === 0) {
        setLayoutPickerOpen(false);
        return;
      }
      setLayoutBusy(true);
      pushSnapshot({ nodes, edges });
      try {
        const positions = await computeAutoLayout(nodes, edges, { algorithm, direction });
        const repositioned = nodes.map((n) => {
          const p = positions.get(n.id);
          if (!p) return n;
          return { ...n, position: p };
        });
        setCanvasState({ nodes: repositioned });
        positions.forEach((p, nodeId) => {
          nodesApi.update(nodeId, { positionX: p.x, positionY: p.y }).catch(() => {});
        });
        setLastLayoutAlgorithm(algorithm);
        setLastLayoutDirection(direction);
        setLayoutPickerOpen(false);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[autoLayout] ELK failed', err);
      } finally {
        setLayoutBusy(false);
      }
    },
    [nodes, edges, pushSnapshot, setCanvasState],
  );

  const handleAddZone = useCallback(
    (position?: { x: number; y: number }) => {
      saveSnapshot();
      const pos = position || {
        x: 250 + Math.random() * 300,
        y: 150 + Math.random() * 200,
      };
      const id = `group-${Date.now()}`;
      const W = 400;
      const H = 250;
      const newNode = {
        id,
        type: 'group',
        position: pos,
        data: { label: 'Nouvelle zone', color: id, width: W, height: H, locked: false },
        style: { width: W, height: H },
        zIndex: -1,
        selectable: true,
        draggable: true,
      };
      useCanvasStore.setState((state) => ({
        nodes: [...state.nodes, newNode as any],
      }));
    },
    [saveSnapshot],
  );

  const handlePaneContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      const flowPos = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      setContextMenu({
        screen: { x: event.clientX, y: event.clientY },
        flow: flowPos,
      });
    },
    [reactFlowInstance],
  );

  // When user draws a connection, don't apply it yet — show link type selector
  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return;
      // Position the selector near the middle of the viewport
      const rect = containerRef.current?.getBoundingClientRect();
      setSelectorPosition({
        x: rect ? rect.left + rect.width / 2 - 90 : 400,
        y: rect ? rect.top + rect.height / 2 - 100 : 300,
      });
      setPendingConnection(connection);
    },
    [],
  );

  const handleLinkTypeSelect = useCallback(
    async (linkType: LinkType) => {
      if (!pendingConnection?.source || !pendingConnection?.target) return;
      saveSnapshot();
      await addLink({
        sourceNodeId: pendingConnection.source,
        targetNodeId: pendingConnection.target,
        type: linkType,
        canvasId,
      });
      setPendingConnection(null);
    },
    [pendingConnection, canvasId, addLink, saveSnapshot],
  );

  const handleEdgeClick = useCallback(
    (event: React.MouseEvent, edge: { id: string; data?: LinkData }) => {
      setEdgeMenuPosition({ x: event.clientX, y: event.clientY });
      setSelectedEdge({
        id: edge.id,
        type: (edge.data?.linkType as LinkType) || LinkType.FreeForm,
      });
    },
    [],
  );

  const handleEdgeChangeType = useCallback(
    async (linkType: LinkType) => {
      if (!selectedEdge) return;
      saveSnapshot();
      await updateEdge(selectedEdge.id, { type: linkType });
      setSelectedEdge(null);
    },
    [selectedEdge, updateEdge, saveSnapshot],
  );

  const handleEdgeDelete = useCallback(async () => {
    if (!selectedEdge) return;
    saveSnapshot();
    await removeEdge(selectedEdge.id);
    setSelectedEdge(null);
  }, [selectedEdge, removeEdge, saveSnapshot]);

  const handleNodesDelete = useCallback(
    async (deletedNodes: { id: string }[]) => {
      saveSnapshot();
      for (const node of deletedNodes) {
        await removeNode(node.id);
      }
    },
    [removeNode, saveSnapshot],
  );

  const handleEdgesDelete = useCallback(
    async (deletedEdges: { id: string }[]) => {
      saveSnapshot();
      for (const edge of deletedEdges) {
        await removeEdge(edge.id);
      }
    },
    [removeEdge, saveSnapshot],
  );

  const handleUndo = useCallback(() => {
    const snapshot = undo({ nodes, edges });
    if (snapshot) {
      setCanvasState({ nodes: snapshot.nodes, edges: snapshot.edges });
    }
  }, [undo, setCanvasState, nodes, edges]);

  const handleRedo = useCallback(() => {
    const snapshot = redo({ nodes, edges });
    if (snapshot) {
      setCanvasState({ nodes: snapshot.nodes, edges: snapshot.edges });
    }
  }, [redo, setCanvasState, nodes, edges]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        handleRedo();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleUndo, handleRedo]);

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'var(--color-text-muted)',
        }}
      >
        Chargement du canvas...
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'var(--color-danger)',
        }}
      >
        Erreur : {error}
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
      <CanvasToolbar onAddNode={handleAddNode} onAddZone={handleAddZone} />
      <Legend />
      <RemoteCursors cursors={remoteCursors} />


      {/* Connected users indicator */}
      {connectedUsers.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 'var(--spacing-md)',
            left: 'var(--spacing-md)',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '4px 10px',
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-md)',
            fontSize: 'var(--font-size-xs)',
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: isConnected ? '#22C55E' : '#EF4444',
            }}
          />
          <span style={{ color: 'var(--color-text-muted)' }}>
            {connectedUsers.length} connecté{connectedUsers.length > 1 ? 's' : ''}
          </span>
          <div style={{ display: 'flex', marginLeft: 4 }}>
            {connectedUsers.slice(0, 5).map((u, i) => (
              <div
                key={i}
                title={`${u.firstName} ${u.lastName}`}
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  backgroundColor: u.color || '#6B7280',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 10,
                  fontWeight: 700,
                  marginLeft: i > 0 ? -6 : 0,
                  border: '2px solid var(--color-surface)',
                }}
              >
                {u.firstName?.[0]}{u.lastName?.[0]}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Unified node inspector (Détails / Relations / Commentaires) */}
      <NodeInspector
        nodeId={inspectedNodeId}
        workspaceId={workspaceId || ''}
        onClose={() => setInspectedNodeId(null)}
        onFocusNode={(id) => {
          setInspectedNodeId(id);
          const target = nodes.find((n) => n.id === id);
          if (target) {
            reactFlowInstance.setCenter(target.position.x + 100, target.position.y + 50, {
              zoom: 1.2,
              duration: 400,
            });
          }
        }}
      />

      {/* History panel */}
      <HistoryPanel
        canvasId={canvasId}
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onSnapshotRestored={() => {
          setHistoryOpen(false);
          window.location.reload();
        }}
      />

      {/* Edge context menu */}
      {selectedEdge && (
        <EdgeContextMenu
          position={edgeMenuPosition}
          currentType={selectedEdge.type}
          onChangeType={handleEdgeChangeType}
          onDelete={handleEdgeDelete}
          onClose={() => setSelectedEdge(null)}
        />
      )}

      {/* Link type selector popup */}
      {pendingConnection && (
        <LinkTypeSelector
          position={selectorPosition}
          onSelect={handleLinkTypeSelect}
          onCancel={() => setPendingConnection(null)}
        />
      )}

      {/* Context menu */}
      {contextMenu && (
        <CanvasContextMenu
          position={contextMenu.screen}
          flowPosition={contextMenu.flow}
          onAddNode={handleAddNodeAtPosition}
          onAddZone={handleAddZone}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Alternate views */}
      {activeView === 'tree' && (
        <div style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, zIndex: 5 }}>
          <TreeView nodes={nodes} edges={edges} onSelectNode={setInspectedNodeId} />
        </div>
      )}

      {activeView === 'table' && (
        <div style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, zIndex: 5 }}>
          <TableView nodes={nodes} onSelectNode={setInspectedNodeId} />
        </div>
      )}

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        style={{
          backgroundColor: 'var(--color-background)',
          visibility: activeView === 'canvas' ? 'visible' : 'hidden',
          pointerEvents: activeView === 'canvas' ? 'auto' : 'none',
        }}
        onEdgeClick={handleEdgeClick}
        onNodeClick={(_event, node) => {
          if (node.type === 'group') return; // no inspector on zones
          setInspectedNodeId(node.id);
        }}
        onPaneClick={() => {
          setInspectedNodeId(null);
          setContextMenu(null);
        }}
        onPaneContextMenu={handlePaneContextMenu}
        onConnect={handleConnect}
        onNodeDragStart={() => {
          dragSnapshotRef.current = { nodes: [...nodes], edges: [...edges] };
        }}
        onNodeDragStop={(_event, node) => {
          if (dragSnapshotRef.current) {
            pushSnapshot(dragSnapshotRef.current);
            dragSnapshotRef.current = null;
          }
          // Persist new position to backend
          nodesApi.update(node.id, {
            positionX: node.position.x,
            positionY: node.position.y,
          });
        }}
        onNodesDelete={handleNodesDelete}
        onEdgesDelete={handleEdgesDelete}
        deleteKeyCode={['Backspace', 'Delete']}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={{
          animated: false,
          type: 'default',
        }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color="var(--color-border)"
        />
        <Controls
          showInteractive={false}
          style={{
            backgroundColor: 'var(--color-surface)',
            borderColor: 'var(--color-border)',
            bottom: 72,
          }}
        />
        <MiniMap style={{ bottom: 72 }} />
      </ReactFlow>

      {/* Bottom toolbar — outside ReactFlow so it's always visible */}
      <div
        className="canvas-bottom-bar"
        style={{
          position: 'absolute',
          bottom: 'var(--spacing-md)',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
          display: 'flex',
          gap: 4,
          padding: '4px 8px',
          backgroundColor: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-md)',
          alignItems: 'center',
        }}
      >
        <button
          onClick={handleUndo}
          disabled={!canUndo()}
          title="Annuler (Ctrl+Z)"
          style={{
            padding: '6px 10px',
            backgroundColor: 'transparent',
            border: 'none',
            color: canUndo() ? 'var(--color-text)' : 'var(--color-text-muted)',
            cursor: canUndo() ? 'pointer' : 'default',
            fontSize: 16,
            opacity: canUndo() ? 1 : 0.4,
          }}
        >
          ↩
        </button>
        <button
          onClick={handleRedo}
          disabled={!canRedo()}
          title="Refaire (Ctrl+Shift+Z)"
          style={{
            padding: '6px 10px',
            backgroundColor: 'transparent',
            border: 'none',
            color: canRedo() ? 'var(--color-text)' : 'var(--color-text-muted)',
            cursor: canRedo() ? 'pointer' : 'default',
            fontSize: 16,
            opacity: canRedo() ? 1 : 0.4,
          }}
        >
          ↪
        </button>
        <div style={{ width: 1, height: 20, backgroundColor: 'var(--color-border)', margin: '0 2px' }} />
        <button
          onClick={() => setHistoryOpen((prev) => !prev)}
          title="Historique"
          style={{
            padding: '6px 10px',
            backgroundColor: historyOpen ? 'var(--color-background)' : 'transparent',
            border: 'none',
            color: 'var(--color-text)',
            cursor: 'pointer',
            fontSize: 16,
            borderRadius: 'var(--radius-md)',
          }}
        >
          &#128339;
        </button>
        <div style={{ width: 1, height: 20, backgroundColor: 'var(--color-border)', margin: '0 2px' }} />
        <ViewSwitcher activeView={activeView} onViewChange={setActiveView} />
        <div style={{ width: 1, height: 20, backgroundColor: 'var(--color-border)', margin: '0 2px' }} />
        <button
          onClick={handleOpenLayoutPicker}
          disabled={nodes.length === 0}
          title="Réorganiser automatiquement"
          style={{
            padding: '6px 10px',
            backgroundColor: 'transparent',
            border: 'none',
            color: nodes.length === 0 ? 'var(--color-text-muted)' : 'var(--color-text)',
            cursor: nodes.length === 0 ? 'default' : 'pointer',
            fontSize: 'var(--font-size-xs)',
            borderRadius: 'var(--radius-md)',
            opacity: nodes.length === 0 ? 0.4 : 1,
          }}
        >
          ⇅ Réorganiser
        </button>
        <div style={{ width: 1, height: 20, backgroundColor: 'var(--color-border)', margin: '0 2px' }} />
        <ExportMenu />
      </div>

      <LayoutPickerDialog
        open={layoutPickerOpen}
        defaultAlgorithm={lastLayoutAlgorithm}
        defaultDirection={lastLayoutDirection}
        busy={layoutBusy}
        onConfirm={handleApplyLayout}
        onCancel={() => !layoutBusy && setLayoutPickerOpen(false)}
      />
    </div>
  );
};

export const StratMapCanvas: FC<StratMapCanvasProps> = (props) => (
  <ReactFlowProvider>
    <StratMapCanvasInner {...props} />
  </ReactFlowProvider>
);

export default StratMapCanvas;
